// NewEventForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../store/useAuthStore3';
import { Event } from './api/api';
import { formatDateTime } from '../../../tools/util';
import { Entity } from '../../../types/entity';
import { FormikSelect } from './components/FormikSelect';
import { Subtenant } from '../../../api/subtenantApi';

export interface NewEventFormProps {
  entities: Entity[];
  subtenants: Subtenant[];
  onSave: (values: Event) => void;
  onCancel: () => void;
}

const NewEventForm: React.FC<NewEventFormProps> = ({
  entities,
  subtenants,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const title = t('NewEventForm.event_new');
  const { customerId } = useAuthStore();


  const initialValues: Event = {
    id: -1,
    registration_number: '',
    entity_id: -1,
    entity_id_1: entities.length == 1 ? entities[0].entity_id : -1,
    entity_id_3: -1,
    ts_start: formatDateTime(new Date()),
    lpr_entity_name: '',
    lpr_group_name: '',
    lpr_subtenant_name: '', 
    matched_number: '',
    verified_number: '',
    car_brand: '',
    car_owmer: '',
    group_id: -1, 
    subtenant_id: -1, 
    tag: 'manual'
  };

  const validationSchema = Yup.object({
    registration_number: Yup.string().required(('registration_number_is_required')),
    entity_id_1: Yup.number().required(('entity_is_required')),
  });

  if (customerId === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{('Loading_form_data')}</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        onSave(values);
      }}
      enableReinitialize
    >
      {(formikProps: FormikProps<Event>) => {
        const { values, setFieldValue } = formikProps;
        return (
          <Form>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <Field name="registration_number">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('NewEventForm.registration_number')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              <FormikSelect
                name="entity_id_1"
                label={t('NewEventForm.entity_Label')}
                placeholder={t('NewEventForm.Select_an entity')}
                options={entities.map((entity) => ({
                  value: entity.entity_id,
                  label: entity.name,
                }))}
              />
              
              <FormikSelect
                name="subtenant_id"
                label={t('NewEventForm.subtenant_Label')}
                placeholder={t('NewEventForm.Select_a subtenant')}
                options={subtenants.map((subtenant) => ({
                  value: subtenant.subtenant_id,
                  label: subtenant.name,
                }))}
              />

              {/* <Field name="entity_id_1">
                {({ field, meta }: { field: any; meta: any }) => (
                  <FormControl fullWidth error={meta.touched && !!meta.error}>
                    <InputLabel id="entity-id-label">{t('NewEventForm.entity_Label')}</InputLabel>
                    <Select
                      labelId="group-id-label"
                      label={t('NewEventForm.entity_Label')}
                      {...field}
                      value={field.value ?? ''}
                    >
                      <MenuItem value=""><em>{t('NewEventForm.Select_an entity')}</em></MenuItem>
                      {entities.map((entity) => (
                        <MenuItem key={entity.entity_id} value={entity.entity_id}>
                          {entity.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.touched && meta.error && (
                      <Typography color="error" variant="caption" component="div">{meta.error}</Typography>
                    )}
                  </FormControl>
                )}
              </Field> */}

              <Field name="tag">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('PlateForm.tag_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    disabled={true}
                  />
                )}
              </Field>              
              
            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined'>
                  {t('NewEventForm.Cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty }>
                  {formikProps.isSubmitting ? t('NewEventForm.Saving') : t('NewEventForm.Save')}
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default NewEventForm;